import { useMemo, useRef } from 'react';
import { useAccount } from '../../contexts/account/AccountContexts';
import { ResourceAccess, UserResource } from '../../types/user/permissions/UserPermissions';

const NO_ACCESS: ResourceAccess = { read: false, write: false };

/**
 * Uprawnienia zalogowanego użytkownika do jednej powierzchni konta. Wzorowane na `useAddon` — jeden
 * hook zamiast rozsianych warunków, żeby zmiana reguły była zmianą w jednym miejscu.
 *
 * Domyślka przy braku danych to BRAK dostępu, nie pełny: zanim `/account/data` wróci, panel ma
 * niczego nie pokazywać niż pokazać zakładkę, którą zaraz schowa — i niż odsłonić rozliczenia
 * komuś, kto nie ma do nich prawa.
 *
 * Wyjątkiem jest PONOWNE zerowanie konta: `AccountContexts.refetchData()` ustawia `accountResponse`
 * na `null` przy każdym odświeżeniu (zdarzenie websocket, zmiana planu, usunięcie projektu), a to
 * nie jest „nie wiemy, kim jesteś" — to „zaraz to samo przyjdzie jeszcze raz". Bez zapamiętania
 * ostatniej znanej wartości kolumny i przyciski akcji znikałyby i wracały przy każdym takim
 * odświeżeniu, także właścicielowi konta. Pamięć jest per użycie hooka, więc komponent montowany
 * dopiero w tym oknie nadal zaczyna od BRAKU dostępu — fail-closed zostaje tam, gdzie ma sens.
 */
const usePermission = (resource: UserResource): ResourceAccess => {
  const { accountResponse } = useAccount();
  const lastKnown = useRef<ResourceAccess | null>(null);

  return useMemo(() => {
    const current = accountResponse?.permissions?.[resource];
    if (current) {
      lastKnown.current = current;
      return current;
    }

    return lastKnown.current ?? NO_ACCESS;
  }, [accountResponse, resource]);
};

export default usePermission;
